import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAuthToken } from '../lib/auth';
import { HOST } from '../config/api';
import styles from './Login.module.css';
import { clearStoredShareInvite, isValidShareInviteCode, storeShareInvite } from '../lib/shareInvite';

export default function ShareLinkHandler({ userId, userInfo, isBootstrapping = false, setUserSubscriptionsUpdated }) {
    const { code } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('checking');
    const [errorMessage, setErrorMessage] = useState('');
    const autoJoinAttempted = useRef(false);
    const shareInvite = useMemo(
        () => ({
            code,
            message:
                'You’ve been invited to join a training plan. Log in or create an account to continue. After signing in, we’ll finish opening the plan for you.',
        }),
        [code]
    );

    const submitJoin = useCallback(async () => {
        if (!isValidShareInviteCode(code)) {
            setStatus('error');
            setErrorMessage('This invite link is invalid. Please check the link and try again.');
            return;
        }

        setStatus('submitting');
        setErrorMessage('');

        try {
            const payload = {
                userId,
                shareCode: code,
            };

            const response = await fetch(HOST + '/v1/subscribe_from_sharelink', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                clearStoredShareInvite();
                setUserSubscriptionsUpdated?.((previousValue) => !previousValue);
                navigate('/profile', { replace: true, state: { fromShare: true } });
                return;
            }

            if (response.status === 401) {
                storeShareInvite(shareInvite);
                navigate('/login', {
                    replace: true,
                    state: {
                        shareInvite,
                    },
                });
                return;
            }

            setStatus('error');
            setErrorMessage('This invite could not be opened. It may be invalid, expired, or temporarily unavailable.');
        } catch (error) {
            setStatus('error');
            setErrorMessage('We could not open the invite right now. Please check your connection and try again.');
            console.error('Failed to open share invite', error);
        }
    }, [code, userId, navigate, shareInvite, setUserSubscriptionsUpdated]);

    useEffect(() => {
        if (!isValidShareInviteCode(code)) {
            setStatus('error');
            setErrorMessage('This invite link is invalid. Please check the link and try again.');
            return;
        }

        setErrorMessage('');

        if (!userId) {
            storeShareInvite(shareInvite);
            navigate('/login', {
                replace: true,
                state: {
                    shareInvite,
                },
            });
            return;
        }

        if (autoJoinAttempted.current) {
            return;
        }

        if (isBootstrapping) {
            setStatus('checking');
            return;
        }

        const hasActiveSubscriptions = Boolean(userInfo?.coachSubscription?.length);

        if (userInfo !== undefined && !hasActiveSubscriptions) {
            autoJoinAttempted.current = true;
            submitJoin();
            return;
        }

        setStatus((currentStatus) => (currentStatus === 'checking' ? 'confirm' : currentStatus));
    }, [code, userId, userInfo, isBootstrapping, navigate, shareInvite, submitJoin]);

    if (status === 'error') {
        return (
            <div className={styles.authPage}>
                <section className={styles.authShell}>
                    <div className={styles.authIntro}>
                        <div className={styles.authImageWrap}>
                            <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                            <div className={styles.authImageOverlay}>
                                <span>Lead the Way</span>
                                <strong>Invite access should feel clear and reliable.</strong>
                                <p>We’ll tell you what went wrong and give you a safe next step instead of leaving you stuck.</p>
                            </div>
                        </div>

                        <div className={styles.authCopy}>
                            <span className={styles.authEyebrow}>Training invite</span>
                            <h1>We couldn&apos;t open this invite.</h1>
                            <p>You can retry the link, log in first, or head back to the app home if you want to start from there.</p>
                        </div>
                    </div>

                    <div className={styles.authPanel}>
                        <div className={styles.successPanel}>
                            <span className={styles.panelKicker}>Invite issue</span>
                            <h2>Something got in the way</h2>
                            <p className={styles.registrationMessageFailure}>{errorMessage}</p>
                            <div className={styles.actionStack}>
                                <button type="button" className={styles.primaryButton} onClick={() => window.location.reload()}>
                                    Try again
                                </button>
                                <Link className={styles.secondaryButton} to="/login" state={{ shareInvite }}>
                                    Go to login
                                </Link>
                                <Link className={styles.secondaryButton} to="/">
                                    Back to home
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (status === 'confirm') {
        return (
            <div className={styles.authPage}>
                <section className={styles.authShell}>
                    <div className={styles.authIntro}>
                        <div className={styles.authImageWrap}>
                            <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                            <div className={styles.authImageOverlay}>
                                <span>Lead the Way</span>
                                <strong>Training invite ready.</strong>
                                <p>Review the invite and choose whether to join before anything changes on your account.</p>
                            </div>
                        </div>

                        <div className={styles.authCopy}>
                            <span className={styles.authEyebrow}>Training invite</span>
                            <h1>You’ve been invited to join a training plan.</h1>
                            <p>Joining will subscribe your account to this plan and make it available from your profile.</p>
                        </div>
                    </div>

                    <div className={styles.authPanel}>
                        <div className={styles.successPanel}>
                            <span className={styles.panelKicker}>Invite</span>
                            <h2>Join this training plan?</h2>
                            <p className={styles.registrationMessageSuccess}>{shareInvite.message}</p>
                            <div className={styles.actionStack}>
                                <button type="button" className={styles.primaryButton} onClick={submitJoin}>
                                    Join plan
                                </button>
                                <Link className={styles.secondaryButton} to="/">
                                    Back to home
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className={styles.authPage}>
            <section className={styles.authShell}>
                <div className={styles.authIntro}>
                    <div className={styles.authImageWrap}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                        <div className={styles.authImageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Opening your training invite.</strong>
                            <p>We’re checking the invite and routing you to the right next step.</p>
                        </div>
                    </div>

                    <div className={styles.authCopy}>
                        <span className={styles.authEyebrow}>Training invite</span>
                        <h1>One moment while we connect the plan.</h1>
                        <p>This should only take a second. We’ll either open the invite or guide you to the right auth step.</p>
                    </div>
                </div>

                <div className={styles.authPanel}>
                    <div className={styles.successPanel}>
                        <span className={styles.panelKicker}>Joining invite</span>
                        <h2>Getting things ready</h2>
                        <p className={styles.registrationMessageSuccess}>
                            {status === 'submitting' ? 'Joining your training plan...' : 'Checking your account...'}
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
